import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { supabase, authEnabled, tellaRoles } from "../lib/supabase";

/**
 * Parent-facing access gate around the whole player. Tella links are shared
 * only with invited families; this makes the invitation real: a grown-up signs
 * in once per device with a Supabase magic link (no password), and only
 * accounts that were added beforehand AND carry the "player" role get in.
 * Studio-only accounts are told this link is for reading, not authoring.
 *
 * Deliberately NOT part of the bedtime ritual: the session persists on the
 * device, so after the first unlock a family goes straight to the Incantation
 * every night. When Supabase isn't configured (local dev) this is a
 * pass-through and the player runs open, exactly as before.
 *
 * Offline grace: bedtime must not break in airplane mode. Once this device has
 * unlocked with a "player" account, a local flag lets it back in when the
 * network (and thus token refresh) is unavailable. The flag gates the UI only —
 * it holds no account data.
 */

const READER_OK_KEY = "tella:reader-ok";

/** The role an account needs to read stories (see story-studio DEPLOY.md § Auth). */
const REQUIRED_ROLE = "player";

/**
 * A failed magic-link landing arrives as #error=…&error_description=… — capture
 * it at MODULE scope (before supabase-js's async URL processing can touch it)
 * so the parent who tapped a dead link sees why instead of a bare form. Error
 * hashes never carry tokens, so stripping the hash here can't eat a sign-in.
 */
const INITIAL_LINK_ERROR: string | null = (() => {
  try {
    const hash = window.location.hash.replace(/^#/, "");
    if (!hash || hash.includes("access_token=")) return null;
    const p = new URLSearchParams(hash);
    const desc = p.get("error_description") || (p.get("error") && "That sign-in link didn't work.");
    if (!desc) return null;
    history.replaceState(null, "", window.location.pathname + window.location.search);
    return `${desc.replace(/\.$/, "")}. Enter your email below for a fresh one.`;
  } catch {
    return null;
  }
})();

export function ParentGate({ children }: { children: ReactNode }) {
  if (!authEnabled || !supabase) return <>{children}</>;
  return <Gate client={supabase}>{children}</Gate>;
}

function readerOkFlag(): boolean {
  try {
    return localStorage.getItem(READER_OK_KEY) === "1";
  } catch {
    return false;
  }
}

function setReaderOkFlag(on: boolean): void {
  try {
    if (on) localStorage.setItem(READER_OK_KEY, "1");
    else localStorage.removeItem(READER_OK_KEY);
  } catch {
    /* storage unavailable → gate still works, just without offline grace */
  }
}

function Gate({ client, children }: { client: SupabaseClient; children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);
  // Once unlocked, stay unlocked for this page's lifetime: a token refresh
  // failing MID-STORY must never yank the book out of a child's hands. The
  // gate re-evaluates on the next load.
  const [latched, setLatched] = useState(false);
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(INITIAL_LINK_ERROR);

  useEffect(() => {
    client.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setReady(true);
    });
    const { data: sub } = client.auth.onAuthStateChange((_e, s) => setSession(s));
    return () => sub.subscription.unsubscribe();
  }, [client]);

  // Remember that this device unlocked with a reader account (offline grace),
  // and latch for this page's lifetime.
  const unlocked = Boolean(session && tellaRoles(session.user).includes(REQUIRED_ROLE));
  useEffect(() => {
    if (unlocked) {
      setReaderOkFlag(true);
      setLatched(true);
    }
  }, [unlocked]);

  if (unlocked || latched) return <>{children}</>;

  // Offline grace: a previously unlocked device shouldn't lose tonight's story
  // to a failed token refresh in airplane mode. Checked BEFORE the ready wait,
  // so an offline cold start (where getSession may stall trying to refresh an
  // expired token) opens the book immediately.
  if (!navigator.onLine && readerOkFlag()) {
    return <>{children}</>;
  }

  if (!ready) {
    // Quiet beat while the stored session loads — no dead spinner, no form flash.
    return <div className="parent-gate" aria-hidden="true" />;
  }

  if (session) {
    // Signed in, but not a reader account (e.g. studio-only).
    return (
      <div className="parent-gate">
        <div className="parent-gate__card">
          <h1>Tella</h1>
          <p>
            This account ({session.user.email}) isn&apos;t set up for story time yet. Ask the
            person who invited you to finish setting it up — or sign in with the email your
            invitation was sent to.
          </p>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setReaderOkFlag(false);
              // Local scope: forget THIS device only. The default (global)
              // would revoke the account's sessions everywhere — e.g. a studio
              // designer checking a family link would be signed out of the
              // studio on their own machine.
              void client.auth.signOut({ scope: "local" });
            }}
          >
            Use a different email
          </button>
        </div>
      </div>
    );
  }

  const signIn = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    const { error } = await client.auth.signInWithOtp({
      email,
      options: {
        // Bring them back to THIS link — family/child/story params intact.
        emailRedirectTo: window.location.href,
        // Only pre-added families may enter: never auto-create an account.
        shouldCreateUser: false,
      },
    });
    setBusy(false);
    if (error) {
      const notInvited =
        /signups?\s+not\s+allowed/i.test(error.message) ||
        (error as { code?: string }).code === "otp_disabled";
      setError(
        notInvited
          ? "That email isn't on the invite list. Double-check it, or ask the person who shared this link."
          : error.message
      );
    } else {
      setSent(true);
    }
  };

  return (
    <div className="parent-gate">
      <div className="parent-gate__card">
        <h1>Tonight&apos;s story is waiting</h1>
        {sent ? (
          <>
            <p>
              We sent a magic link to <strong>{email}</strong>. Open it on this device and the
              book will unlock.
            </p>
            <button type="button" className="parent-gate__again" onClick={() => setSent(false)}>
              Use a different email
            </button>
          </>
        ) : (
          <form onSubmit={signIn}>
            <p>
              A grown-up opens the book: enter the email your Tella invitation was sent to, and
              we&apos;ll send a magic link. Just once on this device.
            </p>
            <label className="parent-gate__label" htmlFor="parent-email">
              Email
            </label>
            <input
              id="parent-email"
              className="parent-gate__input"
              type="email"
              required
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <button type="submit" className="btn btn--start parent-gate__send" disabled={busy}>
              {busy ? "Sending…" : "Send my magic link"}
            </button>
            {error && (
              <p role="alert" className="parent-gate__error">
                {error}
              </p>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
